'use client'

import { Eye, X } from 'lucide-react'
import { useSession } from 'next-auth/react'
import { useState } from 'react'

import { Button } from '@client/components/ui/button'
import { markPostImpersonationExit } from '@client/lib/impersonation-exit-flag'

/**
 * Shown at the top of the app whenever a superadmin is impersonating another
 * user ("acting as" them). Exiting clears the impersonation and returns to the
 * admin console.
 */
export function ImpersonationBanner() {
  const { data: session, update } = useSession()
  const [exiting, setExiting] = useState(false)

  if (!session?.impersonating) return null

  const handleExit = async () => {
    setExiting(true)
    // Drop the impersonated user's project selection so the real admin starts
    // fresh on the pre-impersonation URL (not stuck on whatever workspace they were viewing).
    const returnPath = session.impersonationReturnPath
    await update({ stopImpersonation: true })
    // Hard navigation so the destination loads fresh with the de-impersonated session —
    // avoids page-level superadmin guards bouncing on a stale session.
    const dest = returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//') ? returnPath : '/admin'
    markPostImpersonationExit()
    window.location.assign(dest)
  }

  return (
    <div className="flex items-center justify-center gap-3 bg-amber-500 px-4 py-1.5 text-sm font-medium text-amber-950">
      <span className="flex items-center gap-1.5">
        <Eye className="h-4 w-4" />
        Viewing as <span className="font-semibold">{session.user.email}</span>
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-6 border-amber-700 bg-transparent px-2 text-amber-950 hover:bg-amber-600/40"
        onClick={() => void handleExit()}
        disabled={exiting}
      >
        <X className="mr-1 h-3.5 w-3.5" />
        {exiting ? 'Exiting…' : 'Exit'}
      </Button>
    </div>
  )
}
